import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import type { GetAssetResponse } from "@forgepoint/shared";
import { GetAssetResponseSchema } from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { AssetChip } from "@/components/AssetChip";
import { downloadAsset } from "@/lib/assetUtils";

/**
 * Displays a downloadable asset chip that fetches asset details on mount.
 * Shows a loading state while fetching, then renders an AssetChip.
 * Clicking the chip triggers a download via presigned URL.
 *
 * @param assetId - The ID of the asset to fetch and download
 */
export const AssetDownload = ({ assetId }: { assetId: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asset, setAsset] = useState<GetAssetResponse["data"]["asset"] | null>(
    null,
  );

  useEffect(() => {
    const fetchAsset = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchApi(`api/assets/${assetId}`, "GET");
        const validatedResponse = GetAssetResponseSchema.parse(response);

        if (!validatedResponse.ok) {
          throw new Error("Failed to fetch asset");
        }

        setAsset(validatedResponse.data.asset);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load asset");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAsset();
  }, [assetId]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Loading asset...</span>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="text-xs text-destructive">
        {error || "Failed to load asset"}
      </div>
    );
  }

  return <AssetChip asset={asset} onClick={(a) => downloadAsset(a.id)} />;
};
